import { ConfigurationValueSelect, IConfigurationValueSelect } from './ValuesSelect';
import { FieldTypeEnum } from '@/enums/FieldTypeEnums';
import { NumberTypeEnum } from '@/enums/NumberTypeEnums';
import { ConfigurationAction, IConfigurationAction } from './Action';
import { ApiIdentifers } from '@/config';

export interface IConfigurationField {
    /**
     * The type of the field, see {@link FieldTypeEnum}
     */
    type: FieldTypeEnum;
    /**
     * Postion of the field inside the array
     */
    position: number;
    /**
     * Name of the field
     */
    name: string;
    /**
     * Just for checkboxes an extra short name
     */
    code?: string;
    /**
     * A list of values, e.g. for select fields
     */
    values?: IConfigurationValueSelect[];
    /**
     * A list of ax values, e.g. for select fields from ax data
     */
    axValues?: IConfigurationValueSelect[];
    /**
     * Defines if the field is mandatory
     */
    required: boolean;
    /**
     * The uniqe identifier string of the field
     */
    identifier: string;
    /**
     * Defines the size of steps for number fields
     */
    step?: number;
    /**
     * Minumum value for number fields
     */
    min?: number;
    /**
     * Maximum value for number fields or max length for text-/textarea-fields
     */
    max?: number;
    /**
     * step for double type fields
     */
    rangeStep?: number;
    /**
     * The type of the numeric field, see {@link NumberTypeEnum}
     */
    numericType?: NumberTypeEnum;

    /**
     * A list of optional sub fields
     */
    fields?: IConfigurationField[];
    /**
     * If the field is an action field this property is set
     */
    action?: IConfigurationAction;

    /**
     * Backend sends info if field has subfields and can be closed by default
     * not used yet in frontend
     */
    collapsible: boolean;

    /**
     * Set if field value is a string, in datasets
     */
    valueString?: string;
    /**
     * Set if field value is a number, in datasets
     */
    valueNumber?: number | null;
    /**
     * Set if field value is a boolean, in datasets
     */
    valueBool?: boolean;
    /**
     * The valid status of the field
     */
    isValid: boolean;

    /**
     * Link to the unit object from units store via identifier string
     */
    unitIdentifier?: string;
    /**
     * Link to the unit format field via identifier string
     */
    unit?: string;
    /**
     * The base unit used
     */
    base?: number;
    /**
     * If field has a fixed unit to show in the table header
     */
    fixedUnit?: string;

    /**
     * If input to field should be disabled.
     * Only used in Frontend.
     */
    disabled?: boolean;
}

export interface IConfigurationFieldFunctions {
    /**
     * Method to check if field and sub-field are valid
     */
    isValidField(): boolean;
    /**
     * Method to reset field to default values
     */
    resetFieldValue(): void;
    /**
     * Method to get number of fields that have a value set,
     */
    getSetValuesCounter(): number;
}

export class ConfigurationField implements IConfigurationField, IConfigurationFieldFunctions {
    public type: FieldTypeEnum;
    public position: number;
    public name: string;
    public required: boolean;
    public identifier: string;
    public collapsible: boolean;
    public isValid: boolean;

    public fields?: ConfigurationField[];
    public action?: ConfigurationAction;

    public code?: string;
    public values?: ConfigurationValueSelect[];
    public axValues?: IConfigurationValueSelect[];
    public step?: number;
    public min?: number;
    public max?: number;
    public numericType?: NumberTypeEnum;
    public rangeStep?: number;

    public valueString?: string;
    public valueNumber?: number | null;
    public valueBool?: boolean;

    public unitIdentifier?: string;
    public unit?: string;
    public base?: number;
    public fixedUnit?: string;

    public disabled?: boolean;

    constructor();
    // tslint:disable-next-line: unified-signatures
    constructor(obj: IConfigurationField);
    constructor(obj?: any) {
        this.type = obj && obj.type != null ? obj.type : FieldTypeEnum.TEXT;
        this.position = (obj && obj.position) || 0;
        this.name = (obj && obj.name) || '';
        this.required = (obj && obj.required) || false;
        this.identifier = (obj && obj.identifier) || '';
        this.collapsible = (obj && obj.collapsible) || false;
        this.isValid = (obj && obj.isValid) || true;

        // Note: we need to set these fields to get them reactive in vue
        this.valueString = (obj && obj.valueString) || undefined;
        this.valueNumber = obj && obj.valueNumber != null ? obj.valueNumber : undefined;
        this.valueBool = (obj && obj.valueBool) || undefined;

        // optional properties are only set if used to save space ;)
        if (obj && obj.code != null) {
            this.code = obj.code;
        }
        if (obj && obj.step != null) {
            this.step = obj.step;
        }
        if (obj && obj.min != null) {
            this.min = obj.min;
        }
        if (obj && obj.max != null) {
            this.max = obj.max;
        }
        if (obj && obj.numericType != null) {
            this.numericType = obj.numericType;
        }
        if (obj && obj.rangeStep != null) {
            this.rangeStep = obj.rangeStep;
        }
        if (obj && obj.unitIdentifier != null) {
            this.unitIdentifier = obj.unitIdentifier;
        }
        if (obj && obj.unit != null) {
            this.unit = obj.unit;
        }
        if (obj && obj.base != null) {
            this.base = obj.base;
        }
        if (obj && obj.fixedUnit != null) {
            this.fixedUnit = obj.fixedUnit;
        }

        if (obj && obj.action !== undefined) {
            this.action = new ConfigurationAction(obj.action);
        }

        if (obj && obj.disabled !== undefined) {
            this.disabled = obj.disabled;
        }

        if (obj && obj.type !== undefined) {
            switch (obj.type as FieldTypeEnum) {
                case FieldTypeEnum.SUBFIELD:
                case FieldTypeEnum.ACTION_SUBFIELD:
                    {
                        if (obj.fields !== undefined) {
                            this.fields = [];
                            obj.fields.forEach((field: ConfigurationField) => {
                                this.fields!.push(new ConfigurationField(field));
                            });
                        }
                    }
                    break;
                case FieldTypeEnum.SELECT:
                    {
                        if (obj.values !== undefined) {
                            this.values = [];
                            obj.values.forEach((value: ConfigurationValueSelect) => {
                                this.values!.push(new ConfigurationValueSelect(value));
                            });
                        }
                    }
                    break;
                case FieldTypeEnum.AXSELECT:
                    {
                        if (obj.axValues !== undefined) {
                            this.axValues = [];
                            obj.axValues.forEach((value: ConfigurationValueSelect) => {
                                this.axValues!.push(new ConfigurationValueSelect(value));
                            });
                        }
                    }
                    break;

                default:
                    break;
            }
        }
    }

    /**
     * Method to check if field has a special function
     */
    // tslint:disable-next-line: member-ordering
    public static isSpecialFunction(field: IConfigurationField): boolean {
        let isSpecialFunction = false;
        if (field.type === FieldTypeEnum.CHECKBOX && field.identifier === ApiIdentifers.machineOrderAll) {
            isSpecialFunction = true;
        }

        return isSpecialFunction;
    }

    public isValidField(): boolean {
        let valid = true;

        if ((this.type === FieldTypeEnum.SUBFIELD || this.type === FieldTypeEnum.ACTION_SUBFIELD) && this.fields !== undefined) {
            for (const field of this.fields) {
                if (field.type === FieldTypeEnum.NUMBER && field.unit != null) {
                    const unitField = this.fields.find((f) => f.identifier === field.unit);
                    if (unitField != null && unitField.valueNumber != null && (field.valueNumber == null || isNaN(field.valueNumber))) {
                        valid = false;
                    }
                } else if (field.isValidField() === false) {
                    valid = false;
                }
            }
        } else if (
            this.required === true &&
            // TODO: check if check of this.isValid is neede din some cases...
            // && (this.checkValue() === false || this.isValid === undefined || this.isValid === false)) {
            this.checkValue() === false
        ) {
            this.isValid = false;
            valid = false;
        } else if ((this.type === FieldTypeEnum.TEXT || this.type === FieldTypeEnum.TEXTAREA) && this.max != null && this.max > 0 && this.checkText() === false) {
            this.isValid = false;
            valid = false;
        }

        return valid;
    }

    public resetFieldValue(): void {
        if (this.type === FieldTypeEnum.SUBFIELD && this.fields !== undefined) {
            for (const field of this.fields) {
                field.resetFieldValue();
            }
        } else {
            this.valueString = undefined;
            this.valueNumber = undefined;
            this.valueBool = undefined;
            if (this.type === FieldTypeEnum.UNIT) {
                /**
                 * If we copy a section/field with a unit field within
                 * we also set required to false.
                 * Example: Add a new material section when int the first material
                 * Finess is already set this could lead to a not saveable dataset.
                 */
                this.required = false;
            }
        }
        this.isValidField();
    }

    public getSetValuesCounter(inIdentifier?: string): number {
        let setValueCount = 0;
        if (inIdentifier !== ApiIdentifers.machineOrder) {
            if (this.fields != null) {
                for (const field of this.fields) {
                    setValueCount += field.getSetValuesCounter(inIdentifier);
                }
            } else if (this.valueBool != null || this.valueNumber != null || this.valueString != null) {
                setValueCount += 1;
            }
        } else if (inIdentifier === ApiIdentifers.machineOrder && (this.valueBool != null || this.valueNumber != null || this.valueString != null)) {
            if (this.valueBool) {
                setValueCount += 1;
            } else if (this.valueBool === false && setValueCount > 0) {
                setValueCount--;
            }
        }
        return setValueCount;
    }

    private checkValue(): boolean {
        let isValid = false;
        switch (this.type) {
            case FieldTypeEnum.CHECKBOX:
                isValid = this.valueBool !== undefined ? this.valueBool : false;
                break;
            case FieldTypeEnum.NUMBER:
            case FieldTypeEnum.SELECT:
            case FieldTypeEnum.UNIT:
                isValid = this.valueNumber !== undefined && this.valueNumber !== null ? true : false;
                break;
            case FieldTypeEnum.MYMILL_SELECT:
                isValid = this.valueString !== undefined && this.valueString !== null ? true : false;
                break;
            case FieldTypeEnum.TEXT:
            case FieldTypeEnum.TEXTAREA:
            case FieldTypeEnum.DATE:
            case FieldTypeEnum.DATETIME:
                if (this.valueString !== undefined && this.valueString.length > 0) {
                    if (this.max != null) {
                        isValid = this.valueString.length <= this.max;
                    } else {
                        isValid = true;
                    }
                } else {
                    isValid = false;
                }
                break;
            default:
                break;
        }

        return isValid;
    }

    private checkText(): boolean {
        let isValid = true;

        if (this.required) {
            isValid = this.valueString !== undefined && this.valueString.length > 0 && (this.max != null ? this.valueString.length <= this.max : true);
        } else if (this.valueString != null) {
            isValid = this.valueString.length >= 0 && (this.max != null ? this.valueString.length <= this.max : true);
        }

        return isValid;
    }
}
